import React, { useState, useEffect, forwardRef } from "react";
import { useForm, Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import useFetch from "../../../../../hooks/useFetch";
import { FormControl, FormHelperText } from "@mui/material";
import ShareStepper from "../../../../shares/stepper/Stepper";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import useFormattedLocalDate from "../../../../../hooks/useFormattedLocalDate";
import FileUploader from "../../../../shares/fileUploader/FileUplaoder";
import ModalShell from "../../../../shares/dashboard/ModalShell";

const AddHomework = forwardRef(({ close, get, edit }, ref) => {
  const [call, setCall] = useState({
    AssignmentName: "",
    assignment_headline: "",
    assignment_description: "",
    assignment_available_time_start: "",
    assignment_available_time_end: "",
    assignment_group: { id: null },
    assignment_extra_score: "",
    assignment_gender: "",
    assignment_file: null,
    assignment_answer_file: null,
  });
  const [groups, setGroups] = useState();
  const [activeStep, setActiveStep] = useState(0);
  const formatDate = useFormattedLocalDate();

  const handleNext = (event) => {
    event.preventDefault();
    setActiveStep((prevStep) => prevStep + 1);
  };

  const onSubmit = () => {
    const formData = new FormData();
    formData.append("AssignmentName", call.AssignmentName);
    formData.append("assignment_headline", call.assignment_headline);
    formData.append("assignment_description", call.assignment_description);
    formData.append(
      "assignment_available_time_start",
      call.assignment_available_time_start
    );
    formData.append("assignment_group", call.assignment_group.id);
    formData.append(
      "assignment_available_time_end",
      call.assignment_available_time_end
    );
    formData.append("assignment_extra_score", call.assignment_extra_score);
    formData.append("assignment_gender", call.assignment_gender);
    if (typeof call.assignment_file == "object") {
      formData.append("assignment_file", call.assignment_file);
    }
    if (typeof call.assignment_answer_file == "object") {
      formData.append("assignment_answer_file", call.assignment_answer_file);
    }
    setCall(formData);
    if (edit) {
      editAssignment.reFetch();
    } else {
      newAssignment.reFetch();
    }
  };

  const getGroup = useFetch({
    method: "GET",
    url: `management/groups/?page_size=1000`,
    noHeader: false,
    trigger: true,
    setter: setGroups,
  });

  const newAssignment = useFetch({
    method: "POST",
    url: `management/assignments/`,
    noHeader: false,
    trigger: false,
    data: call,
    caller: get,
  });

  const editAssignment = useFetch({
    method: "PATCH",
    url: `management/assignments/${edit}/`,
    noHeader: false,
    trigger: false,
    data: call,
    caller: get,
  });

  const getAssignment = useFetch({
    method: "GET",
    url: `management/assignments/${edit}/`,
    noHeader: false,
    trigger: edit ? true : false,
    setter: setCall,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: call,
  });

  const steps = ["مرحله اول", "مرحله دوم"];
  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    newAssignment.status === 201 && close();
  }, [newAssignment.status]);

  useEffect(() => {
    editAssignment.status === 200 && close();
  }, [editAssignment.status]);

  return (
    <ModalShell
      ref={ref}
      close={close}
      title={edit ? "ویرایش تکلیف" : "تکلیف جدید"}
      subtitle="فایل‌ها، زمان‌بندی و گروه تکلیف"
      width="max-w-[56rem]"
    >
      <ShareStepper steps={steps} activeStep={activeStep} onNext={handleNext} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-lg text-sm"
      >
        {activeStep === 0 && (
          <>
            <div className="flex gap-x-6 w-full mb-6">
              <FileUploader
                value={call.assignment_file}
                onChange={(file) =>
                  setCall((prev) => ({ ...prev, assignment_file: file }))
                }
                label="بارگزاری تکلیف"
              />

              <FileUploader
                value={call.assignment_answer_file}
                onChange={(file) =>
                  setCall((prev) => ({ ...prev, assignment_answer_file: file }))
                }
                label="بارگزاری پاسخ"
              />
            </div>
            <div className="mb-6">
              <label htmlFor="AssignmentName" className="mb-2xs block text-sm text-ink-muted">
                عنوان
              </label>
              <TextField
                name="AssignmentName"
                value={call.AssignmentName}
                placeholder="عنوان"
                {...register("AssignmentName", {
                  required: "عنوان را وارد کنید",
                })}
                error={!!errors.AssignmentName}
                helperText={
                  errors.AssignmentName ? errors.AssignmentName.message : ""
                }
                onChange={(e) => {
                  setCall({ ...call, AssignmentName: e.target.value });
                }}
                fullWidth
              />
            </div>
            <div className="mb-6">
              <label htmlFor="class" className="mb-2xs block text-sm text-ink-muted">
                مبحث
              </label>
              <FormControl
                error={errors.assignment_headline ? true : false}
                fullWidth
              >
                <Select
                  value={call.assignment_headline}
                  {...register("assignment_headline", {
                    required: "عنوان را وارد کنید",
                  })}
                  fullWidth
                  onChange={(e) => {
                    setCall({ ...call, assignment_headline: e.target.value });
                  }}
                >
                  {lessons.map((lesson) => {
                    return <MenuItem value={lesson}>{lesson}</MenuItem>;
                  })}
                </Select>
                <FormHelperText>{errors?.group_day?.message}</FormHelperText>
              </FormControl>
            </div>
            <div className="mb-6">
              <label htmlFor="assignment_description" className="mb-2xs block text-sm text-ink-muted">
                توضیحات
              </label>
              <TextField
                name="assignment_description"
                value={call.assignment_description}
                placeholder="توضیحات"
                {...register("assignment_description")}
                error={!!errors.assignment_description}
                helperText={
                  errors.assignment_description
                    ? errors.assignment_description.message
                    : ""
                }
                onChange={(e) => {
                  setCall({ ...call, assignment_description: e.target.value });
                }}
                fullWidth
              />
            </div>
            <div className="mb-6" dir="ltr">
              <FormControl
                error={errors.assignment_available_time_start ? true : false}
                fullWidth
              >
                <Controller
                  name="start-day"
                  control={control}
                  render={() => (
                    <MobileDateTimePicker
                      label="روز شروع"
                      value={
                        call.assignment_available_time_start
                          ? new Date(call.assignment_available_time_start)
                          : null
                      }
                      onChange={(e) => {
                        const date = formatDate(e, "datetime");
                        setCall((prev) => ({
                          ...prev,
                          assignment_available_time_start: date,
                        }));
                      }}
                      // onChange={}
                      views={["year", "month", "day", "hours", "minutes"]}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      className="w-full"
                      ampm={false}
                      slotProps={{
                        field: { shouldRespectLeadingZeros: true },
                        toolbar: { hidden: true },
                        tabs: { hidden: true },
                        textField: {
                          InputProps: {
                            sx: { input: { textAlign: "right" } },
                          },
                        },
                      }}
                      sx={{
                        "& .PrivatePickersClockNumber-clockNumber": {
                          fontSize: 50,
                        },
                        "MuiOutlinedInput-input": {
                          textAlign: "right",
                        },
                      }}
                    />
                  )}
                />

                <FormHelperText>
                  {errors?.assignment_available_time_start?.message}
                </FormHelperText>
              </FormControl>
            </div>

            <div className="mb-6" dir="ltr">
              <FormControl
                error={errors.assignment_available_time_end ? true : false}
                fullWidth
              >
                <Controller
                  name="end-day"
                  control={control}
                  render={() => (
                    <MobileDateTimePicker
                      label="روز پایان"
                      value={
                        call.assignment_available_time_end
                          ? new Date(call.assignment_available_time_end)
                          : null
                      }
                      onChange={(e) => {
                        const date = formatDate(e, "datetime");
                        setCall((prev) => ({
                          ...prev,
                          assignment_available_time_end: date,
                        }));
                      }}
                      // onChange={}
                      views={["year", "month", "day", "hours", "minutes"]}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      className="!text-6xl"
                      ampm={false}
                      slotProps={{
                        field: { shouldRespectLeadingZeros: true },
                        toolbar: { hidden: true },
                        tabs: { hidden: true },
                        textField: {
                          InputProps: {
                            sx: { input: { textAlign: "right" } },
                          },
                        },
                      }}
                      sx={{
                        "& .PrivatePickersClockNumber-clockNumber": {
                          fontSize: 50,
                        },
                      }}
                    />
                  )}
                />

                <FormHelperText>
                  {errors?.assignment_available_time_end?.message}
                </FormHelperText>
              </FormControl>
            </div>
            <div className="mb-6">
              <label htmlFor="assignment_extra_score" className="mb-2xs block text-sm text-ink-muted">
                نمره اضافی
              </label>
              <TextField
                type="number"
                name="assignment_extra_score"
                value={call.assignment_extra_score}
                placeholder="نمره اضافی"
                {...register("assignment_extra_score")}
                error={!!errors.assignment_extra_score}
                helperText={
                  errors.assignment_extra_score
                    ? errors.assignment_extra_score.message
                    : ""
                }
                onChange={(e) => {
                  setCall({ ...call, assignment_extra_score: e.target.value });
                }}
                fullWidth
              />
            </div>
          </>
        )}
        {activeStep === 1 && (
          <>
            <div className="mb-6">
              <label htmlFor="class" className="mb-2xs block text-sm text-ink-muted">
                گروه
              </label>
              <FormControl
                error={errors.assignment_group ? true : false}
                fullWidth
              >
                <Select
                  value={call?.assignment_group?.id}
                  {...register("assignment_group", {
                    required: "گروه را وارد کنید",
                  })}
                  fullWidth
                  onChange={(e) => {
                    setCall({
                      ...call,
                      assignment_group: { id: e.target.value },
                    });
                  }}
                >
                  {groups?.results.map((group) => {
                    return <MenuItem value={group.id}>{group.name}</MenuItem>;
                  })}
                </Select>
                <FormHelperText>
                  {errors?.assignment_group?.message}
                </FormHelperText>
              </FormControl>
            </div>
          </>
        )}
        <div className="mt-lg flex justify-end border-t border-rule-subtle pt-lg">
          {isLastStep ? (
            <button
              type="submit"
              className="rounded-soft bg-primary px-2xl py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 motion-reduce:transition-none"
              disabled={editAssignment.loading || newAssignment.loading}
            >
              ثبت تکلیف
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={
                !(
                  call.AssignmentName &&
                  call.assignment_available_time_end &&
                  call.assignment_available_time_start &&
                  call.assignment_answer_file &&
                  call.assignment_file &&
                  call.assignment_headline
                )
              }
              className="rounded-soft bg-primary px-2xl py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 motion-reduce:transition-none"
            >
              بعدی
            </button>
          )}
        </div>
      </form>
    </ModalShell>
  );
});

export default AddHomework;

const digits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const lessons = [
  "توابع و خواص آن ها",
  "معادله درجه دوم",
  "الگو و دنباله",
  "مثلثات",
  "توان های گویا و عبارات جبری",
  "مرور توابع و خواص آن ها",
  "آنالیز",
  "احتمالات",
  "هندسه مختصاتی",
  "معادلات و نامعادلات گویا و گنگ",
  "قدر مطلق",
  "براکت",
  "هندسه",
  "توابع نمایی و لگاریتم",
  "حد",
  "پیوستگی",
  "بخش پذیری",
  "مشتق",
  "کاربرد مشتق",
  "مجانب ها",
  "مقاطع مخروطی",
];
